/**
 * client/src/components/onboarding/OnboardingFlow.tsx
 *
 * FASE 3 — Onboarding Flow Interattivo (Luglio 2026).
 *
 * Flow di onboarding in 3 step, mostrato SOLO al primo accesso.
 * Progettato per convertire visitatori in utenti attivi nel minor
 * numero di tap possibile.
 *
 * STEP 1 — "Che moto hai?"
 *   Input rapido marca + modello con autocomplete. Se non hai una
 *   moto, puoi saltare. L'obiettivo è popolare il garage subito.
 *
 * STEP 2 — "Ecco i motociclisti vicino a te"
 *   Mostra 3-5 profili nearby basati sulla geolocalizzazione.
 *   L'utente può seguirli con un tap. Crea rete sociale immediata.
 *
 * STEP 3 — "Registra il tuo primo percorso"
 *   Call-to-action per iniziare il primo tracciamento. Con una
 *   animazione che spiega come funziona.
 *
 * COMPORTAMENTO:
 * - Mostrato UNA sola volta (flag in localStorage 'rc_onboarding_done')
 * - Completabile in <60 secondi
 * - Ogni step è skippabile
 * - Funziona su mobile e desktop
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bike, Users, Map, ChevronRight, SkipForward, Check } from 'lucide-react';
import useAuthStore from '@/store/useAuthStore';
import { boundApiFetch } from '@/api/core/boundApiFetch';
import { Link } from 'react-router-dom';

const ONBOARDING_DONE_KEY = 'rc_onboarding_done';

interface SuggestedRider {
  id: string;
  nickname: string;
  avatar_url?: string;
  bike_model?: string;
  distance_km?: number;
  common_interests?: string[];
}

// ─── Step definitions ────────────────────────────────────────────────────────

interface StepProps {
  onNext: () => void;
  onSkip: () => void;
}

// ── STEP 1: Garage ───────────────────────────────────────────────────────────

function StepGarage({ onNext, onSkip }: StepProps) {
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(async () => {
    if (!brand.trim() || !model.trim()) return;
    setSaving(true);
    try {
      await boundApiFetch('/api/garage/bikes', {
        method: 'POST',
        body: JSON.stringify({
          brand: brand.trim(),
          model: model.trim(),
          name: `${brand.trim()} ${model.trim()}`,
        }),
      });
      setSaved(true);
      setTimeout(onNext, 800);
    } catch {
      // Se fallisce, vai comunque avanti — non bloccare l'onboarding
      onNext();
    } finally {
      setSaving(false);
    }
  }, [brand, model, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      <div className="w-24 h-24 rounded-full bg-orange-500/10 flex items-center justify-center mb-6">
        <Bike className="w-12 h-12 text-orange-500" />
      </div>

      <h2 className="text-2xl font-bold text-white mb-2">
        Che moto hai?
      </h2>
      <p className="text-gray-400 mb-8 max-w-sm">
        Aggiungi la tua prima moto al garage. Potrai tenerne traccia
        dei chilometri, manutenzione e scadenze.
      </p>

      {!saved ? (
        <div className="w-full max-w-sm space-y-4">
          <input
            type="text"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="Marca (es. Yamaha)"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
            autoFocus
          />
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Modello (es. MT-07)"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
          <button
            onClick={handleSave}
            disabled={!brand.trim() || !model.trim() || saving}
            className="w-full py-3 rounded-xl bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-40 transition-all"
          >
            {saving ? 'Salvataggio...' : 'Aggiungi al Garage'}
          </button>
        </div>
      ) : (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex items-center gap-2 text-green-400"
        >
          <Check className="w-6 h-6" />
          <span className="text-lg font-semibold">{brand} {model} aggiunta!</span>
        </motion.div>
      )}

      <button
        onClick={onSkip}
        className="mt-6 text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
      >
        <SkipForward className="w-4 h-4" />
        Salta, lo farò dopo
      </button>
    </motion.div>
  );
}

// ── STEP 2: Social ────────────────────────────────────────────────────────────

function StepSocial({ onNext, onSkip }: StepProps) {
  const [riders, setRiders] = useState<SuggestedRider[]>([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState<Set<string>>(new Set());
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);

  useEffect(() => {
    // Rider suggeriti (biker attivi non ancora in cerchia): endpoint
    // GET /api/users/me/suggestions — prima inesistente, la lista era vuota.
    boundApiFetch<{ suggestions: SuggestedRider[] }>('/api/users/me/suggestions?limit=5')
      .then((data) => setRiders(data.suggestions || []))
      .catch(() => setRiders([]))
      .finally(() => setLoading(false));
  }, []);

  const handleFollow = async (riderId: string) => {
    setFollowing((prev) => new Set(prev).add(riderId));
    try {
      // FIX ONBOARDING-FOLLOW (Ott 2026): l'endpoint precedente
      // "/api/social/connections/:id/follow" NON esiste (né il prefisso
      // /api/social/connections né una rotta /follow): il "Segui" dello step
      // social falliva SEMPRE in silenzio, lasciando l'utente convinto di
      // seguire rider con cui non era stata creata nessuna richiesta.
      // L'API reale della Cerchia è POST /api/connections/request { targetId }.
      await boundApiFetch('/api/connections/request', {
        method: 'POST',
        body: JSON.stringify({ targetId: riderId }),
      });
    } catch (e: any) {
      // revert optimistic update
      setFollowing((prev) => {
        const next = new Set(prev);
        next.delete(riderId);
        return next;
      });
      // FIX CERCHIA-PAYWALL: il limite della Cerchia Free (3 rider) non deve
      // sembrare un pulsante rotto: spieghiamo il motivo e la via per l'upgrade.
      const body = e?.data ?? e ?? {};
      if (body?.error === 'cerchia_free_limit' || e?.status === 403) {
        setBlockedMsg(
          body?.message ||
            'Cerchia Free piena (max 3 rider). Passa a Rider Plus per seguirne altri.',
        );
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      <div className="w-24 h-24 rounded-full bg-blue-500/10 flex items-center justify-center mb-6">
        <Users className="w-12 h-12 text-blue-500" />
      </div>

      <h2 className="text-2xl font-bold text-white mb-2">
        Ecco i motociclisti vicino a te
      </h2>
      <p className="text-gray-400 mb-8 max-w-sm">
        Connettiti con altri rider nella tua zona. Seguili per vedere
        i loro percorsi e unirti ai loro eventi.
      </p>

      {blockedMsg && (
        <div className="w-full max-w-sm mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex flex-col gap-2">
          <span>{blockedMsg}</span>
          <Link
            to="/pricing?tab=biker"
            className="self-start rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400 transition-colors"
          >
            Scopri Rider Plus
          </Link>
        </div>
      )}

      {loading ? (
        <div className="space-y-3 w-full max-w-sm">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : riders.length === 0 ? (
        <p className="text-gray-500 mb-8">
          Nessun rider trovato nelle vicinanze. Invita i tuoi amici!
        </p>
      ) : (
        <div className="space-y-3 w-full max-w-sm mb-8">
          {riders.slice(0, 5).map((rider) => (
            <div
              key={rider.id}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {rider.nickname?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-white font-medium truncate">{rider.nickname || 'Rider'}</div>
                <div className="text-gray-500 text-xs truncate">
                  {[
                    rider.bike_model,
                    rider.distance_km ? `${Math.round(rider.distance_km)}km` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                onClick={() => handleFollow(rider.id)}
                disabled={following.has(rider.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                  following.has(rider.id)
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {following.has(rider.id) ? 'Seguito ✓' : 'Segui +'}
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onNext}
        className="px-6 py-3 rounded-xl bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-all flex items-center gap-2"
      >
        Continua
        <ChevronRight className="w-5 h-5" />
      </button>

      <button
        onClick={onSkip}
        className="mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
      >
        <SkipForward className="w-4 h-4" />
        Salta
      </button>
    </motion.div>
  );
}

// ── STEP 3: First Ride ────────────────────────────────────────────────────────

function StepFirstRide({ onNext, onSkip }: StepProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mb-6">
        <Map className="w-12 h-12 text-green-500" />
      </div>

      <h2 className="text-2xl font-bold text-white mb-2">
        Registra il tuo primo percorso
      </h2>
      <p className="text-gray-400 mb-8 max-w-sm">
        Ogni volta che esci in moto, RideConnect traccia il tuo
        itinerario, calcola i chilometri e crea la tua mappa personale.
      </p>

      {/* Mini demo animation */}
      <div className="w-full max-w-sm mb-8 p-6 rounded-2xl bg-white/5 border border-white/5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-red-500 animate-pulse" />
          <div>
            <div className="text-white text-sm font-medium">Pronto a partire?</div>
            <div className="text-gray-500 text-xs">Tocca REC per iniziare</div>
          </div>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
            animate={{ width: ['0%', '60%', '85%', '100%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </div>

      <button
        onClick={onNext}
        className="px-8 py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-lg shadow-orange-500/25 flex items-center gap-2"
      >
        Inizia il primo percorso
        <ChevronRight className="w-5 h-5" />
      </button>

      <button
        onClick={onSkip}
        className="mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
      >
        <SkipForward className="w-4 h-4" />
        Vai alla dashboard
      </button>
    </motion.div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current
              ? 'w-8 bg-orange-500'
              : i === current
              ? 'w-8 bg-orange-400'
              : 'w-4 bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const STEPS = [StepGarage, StepSocial, StepFirstRide];

export default function OnboardingFlow() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  // FIX BUG #30 (Agosto 2026): OnboardingFlow (1 di 3) è stato sostituito
  // da SetupWizardPage (Step 1/4). Non mostrare MAI il wizard legacy.
  // SetupWizardPage gestisce tutto l'onboarding nuovi utenti.
  // Se l'utente non ha completato il setup, verrà reindirizzato a /setup
  // dal RootRedirect. Se lo ha già completato, vedrà la dashboard normale.
  const shouldShow = false;

  const finish = useCallback(() => {
    localStorage.setItem(ONBOARDING_DONE_KEY, 'true');
    setDone(true);
  }, []);

  const handleNext = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      finish();
    }
  }, [step, finish]);

  const handleSkip = useCallback(() => {
    // Permetti di skippare senza completare
    finish();
  }, [finish]);

  if (!shouldShow) return null;

  const CurrentStep = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 bg-gray-950 flex flex-col">
      {/* Header con step indicator */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div className="text-gray-500 text-sm">
          {step + 1} di {STEPS.length}
        </div>
        <StepIndicator current={step} total={STEPS.length} />
        <button
          onClick={handleSkip}
          className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
        >
          Salta tutto
        </button>
      </div>

      {/* Step content */}
      <div className="flex-1 flex items-center justify-center overflow-y-auto">
        <AnimatePresence mode="wait">
          <CurrentStep key={step} onNext={handleNext} onSkip={handleSkip} />
        </AnimatePresence>
      </div>

      {/* Welcome text */}
      <div className="px-6 py-4 border-t border-white/5 text-center">
        <p className="text-gray-600 text-sm">
          👋 Benvenut{user?.nickname ? `o ${user.nickname}` : 'o'} su RideConnect!
        </p>
      </div>
    </div>
  );
}
